import BigDrawer from "@/components/Drawer/BigDrawer";
import UserPrivateComponent from "@/components/PrivacyComponent/UserPrivateComponent";
import { loadAllStaff } from "@/redux/rtk/features/hrm/user/userSlice";
import { loadAllAgent } from "@/redux/rtk/features/CRM/agent/agentSlice";
import { useAddLeadMutation, useGetLeadSuggestionsQuery } from "@/redux/rtk/features/leads/leadsApi";
import { useGetLeadSourcesQuery } from "@/redux/rtk/features/leadSource/leadSourceApi";
import { useGetInterestsQuery } from "@/redux/rtk/features/interest/interestApi";

import { useGetMajorsQuery } from "@/redux/rtk/features/major/majorApi";
import { useGetDegreesQuery } from "@/redux/rtk/features/degree/degreeApi";
import { useGetLeadStatusesQuery } from "@/redux/rtk/features/leadStatus/leadStatusApi";
import axios from "axios";
import { NATIONALITIES } from "@/utils/nationalities";
import { COUNTRIES } from "@/utils/countries";

import Button from "@/UI/Button";
import Card from "@/UI/Card";
import { DatePicker, Form, Input, Select, message, AutoComplete, Mentions } from "antd";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { FileSearchOutlined } from "@ant-design/icons";
import { FaWhatsapp } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import AddLeadSource from "../LeadSource/AddLeadSource";
import AddInterest from "../Interest/AddInterest";

import AddMajor from "../LeadSource/AddMajor";
import AddDegree from "../../CRM/Degree/AddDegree";
import AddLeadStatus from "../LeadStatus/AddLeadStatus";
import AddCampaignCode from "../CampaignCode/AddCampaignCode";
import LeadsCreateMany from "./LeadsCreateMany";
import { useGetCampaignCodesQuery } from "@/redux/rtk/features/campaignCode/campaignCodeApi";
import usePermissions from "@/utils/usePermissions";

const { Option } = Select;


export default function AddLeads({ onClose, pageConfig }) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const watchedMobile = Form.useWatch("mobile", form);
  const watchedPhone = Form.useWatch("phone", form);
  const dispatch = useDispatch();
  const roleId = localStorage.getItem("roleId");
  const { list: staffList, loading: staffLoading } = useSelector(
    (state) => state.users
  );

  const { permissions } = usePermissions();
  const canReadAllUser = permissions?.includes("readAll-user");

  const currentUserId = localStorage.getItem("id");
  const currentUserName = localStorage.getItem("user");

  // Create a memoized or computed list of owner options that includes the current user
  const ownerOptions = (canReadAllUser && staffList) ? [...staffList] : [];
  const isCurrentUserInList = ownerOptions.some(item => String(item.id) === String(currentUserId));
  if (!isCurrentUserInList && currentUserId && currentUserName) {
    ownerOptions.push({
      id: parseInt(currentUserId),
      username: currentUserName
    });
  }

  const { data: leadSources, isLoading: leadSourceLoading } =
    useGetLeadSourcesQuery();

  const { data: majors, isLoading: majorLoading, refetch: refetchMajors } =
    useGetMajorsQuery();
  const { data: degrees, isLoading: degreeLoading, refetch: refetchDegrees } =
    useGetDegreesQuery();
  const { data: leadStatuses, isLoading: leadStatusLoading, refetch: refetchLeadStatuses } =
    useGetLeadStatusesQuery();
  const { data: campaignCodes, isLoading: campaignCodeLoading, refetch: refetchCampaignCodes } =
    useGetCampaignCodesQuery();
  const { data: interests, isLoading: interestLoading, refetch: refetchInterests } =
    useGetInterestsQuery();

  const { list: companyList, loading: companyLoading } = useSelector(
    (state) => state.agent
  );

  const { data: setting } = useSelector((state) => state.setting);
  const [fieldConfig, setFieldConfig] = useState({});

  useEffect(() => {
    if (setting?.leadFieldConfig) {
      try {
        setFieldConfig(JSON.parse(setting.leadFieldConfig));
      } catch (e) {
        console.error("Error parsing lead field config", e);
      }
    }
  }, [setting]);

  const isRequired = (key) => {
    return fieldConfig[key] === undefined ? true : fieldConfig[key];
  };

  useEffect(() => {
    dispatch(loadAllStaff({ query: "all" }));
    dispatch(loadAllAgent());
  }, [dispatch]);

  // Set default lead owner to current logged in user
  useEffect(() => {
    const userId = localStorage.getItem("id");
    if (userId) {
      form.setFieldsValue({
        leadOwnerId: parseInt(userId),
      });
    }
  }, [form]);

  // Set default lead status to "virgin"
  useEffect(() => {
    if (leadStatuses && Array.isArray(leadStatuses)) {
      const virginStatus = leadStatuses.find(
        (s) => s.name.toLowerCase() === "virgin"
      );
      if (virginStatus) {
        form.setFieldsValue({
          leadStatus: virginStatus.name,
        });
      }
    }
  }, [leadStatuses, form]);


  const [addLeads, { isLoading }] = useAddLeadMutation();
  const [firstNameKey, setFirstNameKey] = useState("");
  const [lastNameKey, setLastNameKey] = useState("");

  const { data: firstNameSuggestions } = useGetLeadSuggestionsQuery(
    { field: "firstName", key: firstNameKey },
    { skip: !firstNameKey }
  );
  const { data: lastNameSuggestions } = useGetLeadSuggestionsQuery(
    { field: "lastName", key: lastNameKey },
    { skip: !lastNameKey }
  );

  const onFinish = async (values) => {
    try {
      // Prepare form data
      const formData = {
        firstName: values.firstName,
        lastName: values.lastName,
        mobile: values.mobile,
        country: values.country,
        email: values.email,
        phone: values.phone,
        leadOwnerId: values.leadOwnerId,
        leadSourceId: values.leadSourceId,
        nationality: values.nationality,
        gender: values.gender,
        description: values.description,
        interest: values.interest,
        leadStatus: values.leadStatus,
        majorId: values.majorId,
        adId: values.adId,
        campaignCode: values.campaignCode,
        agentId: values.agentId, // This will be companyId
        language_required: values.language_required,
        degree: values.degree,
        country_of_study: values.country_of_study,
        dateOfBirth: values.dateOfBirth ? values.dateOfBirth.format("YYYY-MM-DD") : null,
      };

      const resp = await addLeads(formData);
      if (resp.data) {
        form.resetFields();
        // Reset to default lead owner
        const userId = localStorage.getItem("id");
        if (userId) {
          form.setFieldsValue({
            leadOwnerId: parseInt(userId),
          });
        }
        if (onClose && typeof onClose === 'function') {
          onClose();
        }
      }
    } catch (error) {
      console.error("Error creating lead:", error);
      message.error(t("errors.somethingWentWrong"));
    }
  };

  const [isScanning, setIsScanning] = useState(false);
  const [showPhone, setShowPhone] = useState(false);

  const handleScan = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsScanning(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${import.meta.env.VITE_APP_API}/ocr/scan`, formData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = response.data.data;
      if (data) {
        // Robust name mapping
        const fullNameEn = data.full_name_english || data.full_name || data.full_name_en || "";
        const firstName = data.given_names || (fullNameEn ? fullNameEn.split(" ")[0] : "");
        const lastName = data.surname || (fullNameEn ? fullNameEn.split(" ").slice(1).join(" ") : "");

        // Robust Gender mapping (M/F, ذكر/أنثى, Male/Female)
        let gender = undefined;
        const sexVal = (data.sex || data.sex_arabic || data.gender || "").toString().toLowerCase();
        if (sexVal.startsWith("m") || sexVal.includes("ذكر")) {
          gender = "Male";
        } else if (sexVal.startsWith("f") || sexVal.includes("أنثى")) {
          gender = "Female";
        }

        // Robust Date format handling
        let dob = undefined;
        const dobStr = data.date_of_birth || data.dateOfBirth;
        if (dobStr) {
          dob = dayjs(dobStr, "DD/MM/YYYY");
          if (!dob.isValid()) dob = dayjs(dobStr);
        }

        // Robust Nationality and Country
        const nationality = data.nationality_english || data.nationality || data.nationality_arabic || "";
        const country = data.issuing_country || data.issuing_state || "";

        // Collect all extra info for Description
        const extraInfo = [];
        if (data.passport_number) extraInfo.push(`Passport: ${data.passport_number}`);
        if (data.national_id || data.national_id_number) extraInfo.push(`National ID: ${data.national_id || data.national_id_number}`);
        if (data.profession || data.profession_arabic || data.profession_english) {
          const prof = [data.profession, data.profession_arabic, data.profession_english].filter(Boolean).join(" / ");
          extraInfo.push(`Profession: ${prof}`);
        }
        if (data.full_name_arabic) extraInfo.push(`Name (Ar): ${data.full_name_arabic}`);
        if (data.address || data.address_arabic) extraInfo.push(`Address: ${data.address || data.address_arabic}`);
        if (data.military_status) extraInfo.push(`Military: ${data.military_status}`);
        if (data.place_of_birth_arabic || data.place_of_birth_english || data.place_of_birth) {
          const pob = [data.place_of_birth_arabic, data.place_of_birth_english, data.place_of_birth].filter(Boolean).join(" / ");
          extraInfo.push(`Place of Birth: ${pob}`);
        }

        const idInfo = extraInfo.length > 0 ? `\n--- OCR INFO ---\n${extraInfo.join("\n")}` : "";

        // Map to form fields
        const currentDescription = form.getFieldValue("description") || "";
        form.setFieldsValue({
          firstName: firstName,
          lastName: lastName,
          nationality: nationality,
          country: country,
          gender: gender,
          dateOfBirth: dob && dob.isValid() ? dob : undefined,
          description: currentDescription ? `${currentDescription}${idInfo}` : idInfo.trim(),
        });

        message.success(t("lead.scanSuccess"));
      }
    } catch (error) {
      console.error("Scan error:", error);
      message.error(t("lead.scanError"));
    } finally {
      setIsScanning(false);
      e.target.value = null;
    }
  };

  // Country list
  const countries = COUNTRIES;

  // Nationality list
  const nationalities = NATIONALITIES;

  // Refresh lists after adding new items
  const refreshLists = () => {
    // This will be called after adding new items via BigDrawer
    // The lists are managed in state, so they will update automatically
  };

  return (
    <UserPrivateComponent permission="create-lead">
      <Form
        className="w-full"
        onFinish={onFinish}
        colon={false}
        layout="vertical"
        initialValues={{}}
        form={form}>

        {/* Basic Information Section */}
        <div className="mb-4">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-lg font-semibold">{t("lead.basicInformation")}</h3>
            <div>
              <input
                type="file"
                id="ocr-upload"
                className="hidden"
                onChange={handleScan}
                accept="image/*,.pdf"
              />
              <Button
                onClick={() => document.getElementById("ocr-upload").click()}
                color="primary"
                loading={isScanning}
                icon={<FileSearchOutlined />}
                className="w-auto px-4"
              >
                {t("lead.scanDocument")}
              </Button>
            </div>
          </div>
          <div className="w-full grid grid-cols-2 gap-3">
            <Form.Item
              label={t("lead.firstName")}
              name="firstName"
              rules={[{ required: isRequired("firstName"), message: t("lead.firstNameRequired") }]}>
              <AutoComplete
                options={firstNameSuggestions || []}
                onSearch={(val) => setFirstNameKey(val)}
                onSelect={(val) => form.setFieldsValue({ firstName: val })}
              >
                <Input placeholder={t("lead.firstName")} />
              </AutoComplete>
            </Form.Item>

            <Form.Item
              label={t("lead.lastName")}
              name="lastName"
              rules={[{ required: isRequired("lastName"), message: t("lead.lastNameRequired") }]}>
              <AutoComplete
                options={lastNameSuggestions || []}
                onSearch={(val) => setLastNameKey(val)}
                onSelect={(val) => form.setFieldsValue({ lastName: val })}
              >
                <Input placeholder={t("lead.lastName")} />
              </AutoComplete>
            </Form.Item>

            <Form.Item
              label={t("lead.mobile")}
              required
              rules={[
                { required: isRequired("mobile"), message: t("lead.mobileRequired") },
                { pattern: /^\+?[0-9]+$/, message: t("lead.invalidPhoneNumber") }
              ]}>
              <div className="flex items-center gap-2">
                <Form.Item name="mobile" noStyle normalize={(value) => value.replace(/[^0-9+]/g, "")}>
                  <Input
                    placeholder="+1 (273) 642-1785"
                    style={{ width: "100%" }}
                  />
                </Form.Item>
                {watchedMobile && (
                  <a
                    href={`https://wa.me/${watchedMobile.toString().replace(/[^0-9+]/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center min-w-[40px] h-10 rounded-lg bg-green-500 hover:bg-green-600 text-white transition-colors"
                    title="Open in WhatsApp">
                    <FaWhatsapp className="text-lg" />
                  </a>
                )}
              </div>
            </Form.Item>


            {/* Optional second number */}
            {!showPhone ? (
              <Form.Item label=" " colon={false}>
                <button
                  type="button"
                  onClick={() => setShowPhone(true)}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#1677ff",
                    cursor: "pointer",
                    padding: 0,
                    fontSize: 13,
                  }}
                >
                  + {t("lead.addAnotherNumber")}
                </button>
              </Form.Item>
            ) : (
              <Form.Item
                label={
                  <span>
                    {t("lead.phoneNumber")}
                    <button
                      type="button"
                      onClick={() => { setShowPhone(false); form.setFieldValue("phone", undefined); }}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#ff4d4f",
                        cursor: "pointer",
                        marginInlineStart: 8,
                        fontSize: 12,
                      }}
                    >
                      ✕
                    </button>
                  </span>
                }
              >
                <div className="flex items-center gap-2">
                  <Form.Item
                    name="phone"
                    noStyle
                    normalize={(value) => value.replace(/[^0-9+]/g, "")}
                    rules={[
                      { required: isRequired("phone"), message: t("common.required") },
                      { pattern: /^\+?[0-9]+$/, message: t("lead.invalidPhoneNumber") }
                    ]}>
                    <Input placeholder="+1 (273) 642-1785" style={{ width: "100%" }} />
                  </Form.Item>
                  {watchedPhone && (
                    <a
                      href={`https://wa.me/${watchedPhone.toString().replace(/[^0-9+]/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center min-w-[40px] h-10 rounded-lg bg-green-500 hover:bg-green-600 text-white transition-colors"
                      title="Open in WhatsApp">
                      <FaWhatsapp className="text-lg" />
                    </a>
                  )}
                </div>
              </Form.Item>
            )}

            <Form.Item label={t("lead.country")} name="country" rules={[{ required: isRequired("country"), message: t("common.required") }]}>
              <Select
                style={{ width: "100%" }}
                allowClear
                showSearch
                optionFilterProp="children"
                placeholder={t("lead.country")}>
                {countries.map((item) => (
                  <Select.Option key={item.value} value={item.value}>
                    {item.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label={t("lead.country_of_study")} name="country_of_study" rules={[{ required: isRequired("country_of_study"), message: t("common.required") }]}>
              <Select
                style={{ width: "100%" }}
                allowClear
                showSearch
                optionFilterProp="children"
                placeholder={t("lead.country_of_study")}>
                {countries.map((item) => (
                  <Select.Option key={item.value} value={item.value}>
                    {item.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item style={{ width: "100%" }} label={t("common.email")} name="email" rules={[{ type: "email", message: t("lead.invalidEmail") }, { required: isRequired("email"), message: t("common.required") }]}>
              <Input placeholder={t("common.email")} />
            </Form.Item>

            <Form.Item label={t("lead.gender")} name="gender" rules={[{ required: isRequired("gender"), message: t("common.required") }]}>
              <Select placeholder={t("lead.selectGender")}>
                <Option value="Male">{t("lead.male")}</Option>
                <Option value="Female">{t("lead.female")}</Option>
              </Select>
            </Form.Item>

            <Form.Item label={t("lead.dateOfBirth")} name="dateOfBirth" rules={[{ required: isRequired("dateOfBirth"), message: t("common.required") }]}>
              <DatePicker
                style={{ width: "100%" }}
                format="DD/MM/YYYY"
                placeholder="DD/MM/YYYY"
              />
            </Form.Item>
          </div>
        </div>

        {/* Lead Information Section */}
        <div className="mb-4">
          <h3 className="text-lg font-semibold mb-3">{t("lead.leadInformation")}</h3>
          <div className="w-full grid grid-cols-2 gap-3">
            <Form.Item
              label={t("lead.leadOwner")}
              name="leadOwnerId"
              rules={[{ required: isRequired("leadOwnerId"), message: t("lead.selectLeadOwner") }]}>
              <Select
                loading={staffLoading}
                allowClear
                showSearch
                optionFilterProp="children"
                placeholder={t("lead.selectLeadOwner")}>
                {ownerOptions?.map((item) => (
                  <Select.Option key={item.id} value={item.id}>
                    {item.username}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label={
                <>
                  {t("lead.leadSource")}
                  <BigDrawer title={t("lead.leadSource")}>
                    <AddLeadSource drawer={true} />
                  </BigDrawer>
                </>
              }
              name={"leadSourceId"}
              rules={[{ required: isRequired("leadSourceId"), message: t("lead.leadSourceRequired") }]}>
              <Select
                style={{ width: "100%" }}
                loading={leadSourceLoading}
                allowClear
                showSearch
                optionFilterProp="children"
                placeholder={t("lead.selectLeadSource")}>
                {leadSources?.map((item) => (
                  <Select.Option key={item.id} value={item.id}>
                    {item?.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label={t("lead.nationality")} name="nationality" rules={[{ required: isRequired("nationality"), message: t("common.required") }]}>
              <Select
                style={{ width: "100%" }}
                allowClear
                showSearch
                placeholder={t("lead.selectNationality")}
                optionFilterProp="label"
                filterOption={(input, option) =>
                  (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                }
                options={nationalities}
              />
            </Form.Item>


            <Form.Item label={t("lead.description")} name="description" rules={[{ required: isRequired("description"), message: t("common.required") }]}>
              <Mentions
                rows={3}
                placeholder={t("lead.notePlaceholder") || "Enter description here... Use @ to mention staff"}
              >
                {staffList?.map((item) => (
                  <Mentions.Option key={item.id} value={item.username}>
                    {item.username}
                  </Mentions.Option>
                ))}
              </Mentions>
            </Form.Item>



            <Form.Item
              label={
                <>
                  {t("lead.interest")}
                  <BigDrawer title={t("lead.interest")} onClose={refetchInterests}>
                    <AddInterest drawer={true} onClose={refetchInterests} />
                  </BigDrawer>
                </>
              }
              name={"interest"}
              rules={[{ required: isRequired("interest"), message: t("lead.interestRequired") }]}>
              <Select
                style={{ width: "100%" }}
                loading={interestLoading}
                allowClear
                showSearch
                placeholder={t("lead.selectInterest")}>
                {interests?.map((item) => (
                  <Select.Option key={item.id} value={item.id}>
                    {item?.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label={
                <>
                  {t("lead.leadStatus")}
                  <BigDrawer title={t("lead.leadStatus")} onClose={refetchLeadStatuses}>
                    <AddLeadStatus drawer={true} onClose={refetchLeadStatuses} />
                  </BigDrawer>
                </>
              }
              name={"leadStatus"}
              rules={[{ required: isRequired("leadStatus"), message: t("common.required") }]}>
              <Select
                style={{ width: "100%" }}
                loading={leadStatusLoading}
                allowClear
                showSearch
                placeholder={t("lead.selectLeadStatus")}
                optionLabelProp="label"
                filterOption={(input, option) =>
                  (option?.labelString ?? "").toLowerCase().includes(input.toLowerCase())
                }>
                {leadStatuses?.map((item) => (
                  <Select.Option
                    key={item.id}
                    value={item.name} // Use name for leadStatus varchar column
                    labelString={item.name || ""}
                    label={
                      <div className="flex items-center gap-2">
                        <span
                          style={{
                            display: "inline-block",
                            width: "12px",
                            height: "12px",
                            backgroundColor: item.color,
                            borderRadius: "2px",
                          }}
                        />
                        {item.name}
                      </div>
                    }>
                    <div className="flex items-center gap-2">
                      <span
                        style={{
                          display: "inline-block",
                          width: "12px",
                          height: "12px",
                          backgroundColor: item.color,
                          borderRadius: "2px",
                        }}
                      />
                      {item.name}
                    </div>
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label={
                <>
                  {t("lead.major")}
                </>
              }
              name="majorId"
              rules={[{ required: isRequired("majorId"), message: t("lead.majorRequired") }]}>
              <Select
                style={{ width: "100%" }}
                loading={majorLoading}
                allowClear
                showSearch
                optionFilterProp="children"
                placeholder={t("lead.selectMajor")}>
                {majors?.map((item) => (
                  <Select.Option key={item.id} value={item.id}>
                    {item.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>
        </div>

        {/* Additional Information Section */}
        <div className="mb-4">
          <h3 className="text-lg font-semibold mb-3">{t("lead.additionalInformation")}</h3>
          <div className="w-full grid grid-cols-2 gap-3">
            <Form.Item label={t("lead.adId")} name="adId" rules={[{ required: isRequired("adId"), message: t("common.required") }]}>
              <Input placeholder={t("lead.enterAdId")} />
            </Form.Item>

            <Form.Item
              label={
                <>
                  {t("lead.campaignCode")}
                  <BigDrawer title={t("campaignCode.createCampaignCode") || "Create Campaign Code"} onClose={refetchCampaignCodes}>
                    <AddCampaignCode drawer={true} onClose={refetchCampaignCodes} />
                  </BigDrawer>
                </>
              }
              name="campaignCode"
              rules={[{ required: isRequired("campaignCode"), message: t("lead.campaignCodeRequired") }]}
            >
              <Select
                style={{ width: "100%" }}
                loading={campaignCodeLoading}
                allowClear
                showSearch
                placeholder={t("lead.selectCampaignCode") || "Select campaign code"}
              >
                {campaignCodes?.map((item) => (
                  <Select.Option key={item.id} value={item.name}>
                    {item.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label={t("lead.agent")} name="agentId" hidden>
              <Select
                style={{ width: "100%" }}
                loading={companyLoading}
                allowClear
                showSearch
                placeholder={t("lead.selectCompany")}>
                {companyList?.map((item) => (
                  <Select.Option key={item.id} value={item.id}>
                    {item?.companyName}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item label={t("lead.requiredLanguage")} name="language_required" rules={[{ required: isRequired("language_required"), message: t("common.required") }]}>
              <Select
                style={{ width: "100%" }}
                allowClear
                placeholder={t("lead.selectLanguage")}>
                <Option value="en">{t("lead.languages.en")}</Option>
                <Option value="tr">{t("lead.languages.tr")}</Option>
                <Option value="ar">{t("lead.languages.ar")}</Option>
                <Option value="fr">{t("lead.languages.fr")}</Option>
                <Option value="ru">{t("lead.languages.ru")}</Option>
              </Select>
            </Form.Item>

            <Form.Item
              label={
                <>
                  {t("lead.degree")}
                  <BigDrawer title={t("lead.degree")} onClose={refetchDegrees}>
                    <AddDegree onClose={refetchDegrees} />
                  </BigDrawer>
                </>
              }
              name="degree"
              rules={[{ required: isRequired("degree"), message: t("common.required") }]}>
              <Select
                style={{ width: "100%" }}
                loading={degreeLoading}
                allowClear
                showSearch
                optionFilterProp="children"
                placeholder={t("lead.selectDegree")}>
                {degrees?.map((item) => (
                  <Select.Option key={item.id} value={item.name}>
                    {item.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>
        </div>

        <Form.Item label="">
          <div className="flex items-center gap-2">
            <Button
              type="submit"
              color="primary"
              loading={isLoading}>
              {t("common.create")}
            </Button>
          </div>
        </Form.Item>
      </Form >
      <Card
        className="my-5"
        title={<span className="text-center font-bold">{t("lead.importFromCSV")}</span>}>
        <LeadsCreateMany
          urlPath={"lead"}
          demoData={[
            [
              "First Name*",
              "Last Name*",
              "Mobile*",
              "Email",
              "Phone",
              "Lead Owner*",
              "Lead Source*",
              "Major*",
              "Status",
              "Country of Study",
            ],
            [
              "John",
              "Doe",
              "01708888842",
              "john@example.com",
              "01708888842",
              staffList?.[0]?.username || "admin",
              leadSources?.[0]?.name || "Facebook",
              majors?.[0]?.name || "Computer Science",
              "new",
              "Turkey",
            ],
          ]}
          invalidTags={["Leads"]}
          title={t("lead.demoLeads")}
        />
      </Card>
    </UserPrivateComponent >
  );
}
